import { createContext, useReducer, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { SessionTypeInDatabase } from '@/utils/getFirebaseSessionTypes';
import { SensorInDatabaseWithTitle } from '@/utils/getFirebaseSystemByConfigId';

import { SensorEventMap } from '@/controllers/interfaces/IEventDispatcher';
import { hubSensorService, sensorService } from '@/controllers/sensorService';

interface LocationState {
  clientId: string;
  date: string;
  therapistId: string;
  clientName: string | null;
  sessionId: string;
  tests: string[];
  session: SessionTypeInDatabase;
}

// Extend state interface to include configuration
export interface SensorsState {
  allConnected: boolean;
  allSuccessfullyCalibrated: boolean;
  allSynced: boolean;
  batteryLevel: Map<string, number>;
  connectedSensors: string[];
  failedCalibrationSensors: string[];
  failedSensors: string[];
  isCalibrating: boolean;
  isRecording: boolean;
  sensorsInDatabase: SensorInDatabaseWithTitle[];
  networkKey: number;
  deviceState: string; // Hex string containing the config of the sensors
  sessionStarted: boolean;
}

export type HubStatus = 'disconnected' | 'connecting' | 'connected';

// Initial state with default values
const initialState: SensorsState = {
  allConnected: false,
  allSuccessfullyCalibrated: false,
  allSynced: false,
  batteryLevel: new Map<string, number>(),
  connectedSensors: [],
  failedCalibrationSensors: [],
  failedSensors: [],
  isCalibrating: false,
  isRecording: false,
  sensorsInDatabase: [],
  networkKey: 0, // Default network key
  deviceState: '0x00F84400',
  sessionStarted: false,
};

// Define all possible actions for state updates,
// including a new MARK_SENSOR_FAILED action.
type SensorsAction =
  | { type: 'SET_CALIBRATING'; payload: boolean }
  | { type: 'SET_ALL_CONNECTED'; payload: boolean }
  | { type: 'SET_ALL_SUCCESSFULLY_CALIBRATED'; payload: boolean }
  | { type: 'SET_ALL_SYNCED'; payload: boolean }
  | { type: 'SET_FAILED_CALIBRATION_SENSORS'; payload: string[] }
  | { type: 'SET_SENSORS_IN_DATABASE'; payload: SensorInDatabaseWithTitle[] }
  | { type: 'SET_NETWORK_KEY'; payload: number }
  | { type: 'SET_DEVICE_STATE'; payload: string }
  | { type: 'MARK_SENSOR_FAILED'; payload: string }
  | { type: 'MARK_SENSOR_WORKING'; payload: string }
  | { type: 'DISCONNECT_SENSORS' }
  | { type: 'CONNECT_SENSOR'; payload: string }
  | { type: 'FINISH_CALIBRATION' }
  | { type: 'SET_BATTERY_LEVEL'; payload: { sensorId: string; batteryLevel: number } }
  | { type: 'RECONFIGURE_SENSORS' }
  | { type: 'MARK_CALIBRATION_OF_SENSOR_FAILED'; payload: string }
  | { type: 'SET_RECORDING'; payload: boolean }
  | { type: 'SET_SESSION_STARTED'; payload: boolean }
  | { type: 'RESET' };

// Reducer function handling state transitions
function sensorsReducer(state: SensorsState, action: SensorsAction): SensorsState {
  switch (action.type) {
    case 'SET_CALIBRATING':
      return { ...state, isCalibrating: action.payload };

    case 'SET_ALL_CONNECTED':
      return { ...state, allConnected: action.payload };

    case 'SET_ALL_SUCCESSFULLY_CALIBRATED':
      return { ...state, allSuccessfullyCalibrated: action.payload };

    case 'SET_FAILED_CALIBRATION_SENSORS':
      return { ...state, failedCalibrationSensors: action.payload };

    case 'SET_SENSORS_IN_DATABASE':
      return { ...state, sensorsInDatabase: action.payload };

    case 'SET_NETWORK_KEY':
      return { ...state, networkKey: action.payload };

    case 'SET_DEVICE_STATE':
      return { ...state, deviceState: action.payload };

    case 'DISCONNECT_SENSORS': {
      return {
        ...state,
        connectedSensors: [],
      };
    }

    case 'CONNECT_SENSOR':
      // Only add if the sensor isn't already connected
      if (state.connectedSensors.includes(action.payload)) {
        return state;
      }

      return {
        ...state,
        connectedSensors: [...state.connectedSensors, action.payload],
      };

    case 'MARK_SENSOR_FAILED':
      // Only add if the sensor isn't already marked
      if (state.failedSensors.includes(action.payload)) {
        return state;
      }

      return {
        ...state,
        failedSensors: [...state.failedSensors, action.payload],
      };

    case 'MARK_CALIBRATION_OF_SENSOR_FAILED':
      // Only add if the sensor isn't already marked
      if (state.failedCalibrationSensors.includes(action.payload)) {
        return state;
      }

      return {
        ...state,
        failedCalibrationSensors: [...state.failedCalibrationSensors, action.payload],
      };

    case 'MARK_SENSOR_WORKING':
      // remove the sensor if it is present in the failed calibration sensors array
      return {
        ...state,
        failedSensors: state.failedSensors.filter((sensor) => sensor !== action.payload),
      };

    case 'FINISH_CALIBRATION':
      return {
        ...state,
        allSuccessfullyCalibrated: state.failedCalibrationSensors.length === 0,
        isCalibrating: false,
      };

    case 'SET_ALL_SYNCED':
      return { ...state, allSynced: action.payload };

    case 'SET_BATTERY_LEVEL': {
      const next = new Map(state.batteryLevel);
      next.set(action.payload.sensorId, action.payload.batteryLevel);
      return { ...state, batteryLevel: next };
    }

    case 'RECONFIGURE_SENSORS':
      return {
        ...state,
        isCalibrating: false,
        isRecording: false,
        allSuccessfullyCalibrated: false,
        allSynced: false,
        connectedSensors: [],
        failedSensors: [],
        failedCalibrationSensors: [],
        allConnected: false,
        batteryLevel: new Map<string, number>(),
      };

    case 'SET_RECORDING':
      return { ...state, isRecording: action.payload };

    case 'SET_SESSION_STARTED':
      return { ...state, sessionStarted: action.payload };

    case 'RESET':
      return {
        ...initialState,
        batteryLevel: new Map<string, number>(),
      };

    default:
      return state;
  }
}

interface SensorsContextType {
  disconnectSensors: VoidFunction;
  finishCalibration: VoidFunction;
  hubBatteryLevel: number | undefined;
  hubStatus: HubStatus;
  markSensorFailed: (sensorId: string) => void;
  markSensorWorking: (sensorId: string) => void;
  reconfigureSensors: VoidFunction;
  reset: VoidFunction;
  resetCalibration: VoidFunction;
  sensorConnected: (macAddress: string) => void;
  sensorFailedCalibration: (sensorId: string) => void;
  setAllConnected: (allConnected: boolean) => void;
  setAllSynced: (allSynced: boolean) => void;
  setBatteryLevel: (sensorId: string, batteryLevel: number) => void;
  setHubStatus: (status: HubStatus) => void;
  setSensorsInDatabase: (config: SensorInDatabaseWithTitle[]) => void;
  setNetworkKey: (networkKey: number) => void;
  setDeviceState: (deviceState: string) => void;
  setRecording: (isRecording: boolean) => void;
  startCalibration: VoidFunction;
  state: SensorsState;
  setSessionStarted: (sessionStarted: boolean) => void;
}

export const SensorsContext = createContext<SensorsContextType>({
  disconnectSensors: () => undefined,
  finishCalibration: () => undefined,
  hubBatteryLevel: undefined,
  hubStatus: 'disconnected',
  markSensorFailed: () => undefined,
  markSensorWorking: () => undefined,
  reconfigureSensors: () => undefined,
  reset: () => undefined,
  resetCalibration: () => undefined,
  sensorConnected: () => undefined,
  sensorFailedCalibration: () => undefined,
  setAllConnected: () => undefined,
  setAllSynced: () => undefined,
  setBatteryLevel: () => undefined,
  setHubStatus: () => undefined,
  setSensorsInDatabase: () => undefined,
  setNetworkKey: () => undefined,
  setDeviceState: () => undefined,
  startCalibration: () => undefined,
  setRecording: () => undefined,
  state: initialState,
  setSessionStarted: () => undefined,
});

export const SensorsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(sensorsReducer, initialState);
  const [hubStatus, setHubStatus] = useState<HubStatus>('disconnected');
  const [hubBatteryLevel, setHubBatteryLevel] = useState<number | undefined>(undefined);

  const { state: locationState } = useLocation<LocationState | undefined>();
  const isHubFlow = useMemo(
    () => locationState?.session?.title === 'Gait Observations',
    [locationState?.session?.title],
  );

  // Start calibration: resets failed sensors and calibration status.
  const startCalibration = useCallback(() => {
    dispatch({ type: 'SET_FAILED_CALIBRATION_SENSORS', payload: [] });
    dispatch({ type: 'SET_ALL_SUCCESSFULLY_CALIBRATED', payload: false });
    dispatch({ type: 'SET_CALIBRATING', payload: true });
  }, []);

  // Mark all sensors as connected.
  const setAllConnected = useCallback((allConnected: boolean) => {
    dispatch({ type: 'SET_ALL_CONNECTED', payload: allConnected });
  }, []);

  // Disconnect sensors by marking them as not connected.
  const disconnectSensors = useCallback(() => {
    dispatch({ type: 'DISCONNECT_SENSORS' });
    dispatch({ type: 'SET_ALL_CONNECTED', payload: false });
  }, []);

  // Mark an individual sensor as having failed calibration via reducer.
  const markSensorFailed = useCallback((sensorId: string) => {
    dispatch({ type: 'MARK_SENSOR_FAILED', payload: sensorId });
  }, []);

  const markSensorWorking = useCallback((sensorId: string) => {
    dispatch({ type: 'MARK_SENSOR_WORKING', payload: sensorId });
  }, []);

  // Finish calibration: if there are any failed sensors, then calibration is not successful.
  const finishCalibration = useCallback(() => {
    dispatch({ type: 'FINISH_CALIBRATION' });
  }, [state.failedSensors]);

  const setSensorsInDatabase = useCallback((sensorsInDatabase: SensorInDatabaseWithTitle[]) => {
    dispatch({ type: 'SET_SENSORS_IN_DATABASE', payload: sensorsInDatabase });
  }, []);

  const setNetworkKey = useCallback((networkKey: number) => {
    dispatch({ type: 'SET_NETWORK_KEY', payload: networkKey });
  }, []);

  const setDeviceState = useCallback((deviceState: string) => {
    dispatch({ type: 'SET_DEVICE_STATE', payload: deviceState });
  }, []);

  const sensorConnected = useCallback((macAddress: string) => {
    dispatch({ type: 'CONNECT_SENSOR', payload: macAddress });
  }, []);

  const setSessionStarted = useCallback((sessionStarted: boolean) => {
    dispatch({ type: 'SET_SESSION_STARTED', payload: sessionStarted });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
    setHubStatus('disconnected');
    setHubBatteryLevel(undefined);
  }, []);

  const setAllSynced = useCallback(
    async (allSynced: boolean) => {
      dispatch({ type: 'SET_ALL_SYNCED', payload: allSynced });

      if (isHubFlow) {
        try {
          await hubSensorService.startOffsetCompensation();
          console.log('Offset compensation started successfully');
        } catch (error) {
          console.error('Failed to start offset compensation:', error);
        }
      }
    },
    [isHubFlow],
  );

  const setBatteryLevel = useCallback((sensorId: string, batteryLevel: number) => {
    dispatch({ type: 'SET_BATTERY_LEVEL', payload: { sensorId: sensorId, batteryLevel } });
  }, []);

  const reconfigureSensors = useCallback(async () => {
    dispatch({ type: 'RECONFIGURE_SENSORS' });
    await sensorService.disconnectDevices();
  }, []);

  const setRecording = useCallback((isRecording: boolean) => {
    dispatch({ type: 'SET_RECORDING', payload: isRecording });
  }, []);

  // MARK: Event listeners
  const sensorFailedCalibration = useCallback((sensorId: string) => {
    dispatch({ type: 'MARK_CALIBRATION_OF_SENSOR_FAILED', payload: sensorId });
  }, []);

  const resetCalibration = useCallback(() => {
    dispatch({ type: 'SET_FAILED_CALIBRATION_SENSORS', payload: [] });
    dispatch({ type: 'SET_ALL_SUCCESSFULLY_CALIBRATED', payload: false });
    dispatch({ type: 'SET_CALIBRATING', payload: false });

    const service = isHubFlow ? hubSensorService : sensorService;
    service.emit('calibrationReset');
  }, [isHubFlow]);

  // MARK: Add event listeners for emitter state changes
  // * We do this here because these events need to be handled across screens
  useEffect(() => {
    function handleBatteryUpdate(event: SensorEventMap['batteryUpdate']) {
      setBatteryLevel(event.macAddress, event.battery);

      // ? We might only want to do this in the hub flow, but battery updates are only emitted when the sensor is connected
      // ? Hub flow doesn't send a battery update when the sensor is connected
      handleDeviceConnected(event);
    }

    function handleDeviceConnected(event: SensorEventMap['deviceConnected']) {
      sensorConnected(event.macAddress);
      markSensorWorking(event.macAddress);
    }

    function handleDeviceDisconnected(event: SensorEventMap['deviceDisconnected']) {
      markSensorFailed(event.macAddress);
    }

    /**
     * Handles the hub battery update event by logging the update,
     * setting the hub status to 'connected', and updating the hub battery level.
     *
     * @param event - The hub battery update event containing the battery level.
     */
    function handleHubBatteryUpdate(event: SensorEventMap['hubBatteryUpdate']) {
      setHubStatus('connected');
      setHubBatteryLevel(event.battery);
    }

    /**
     * Handles the loss of connection to the sensor hub.
     *
     * This listener is invoked when the `hubDisconnected` event fires,
     * indicating that the central hub is no longer reachable or has powered off.
     * It logs the disconnection, updates UI state, and clears any stale battery info.
     *
     * @param event - Payload of the `hubDisconnected` event (always `void`)
     */
    function handleHubDisconnected(event: SensorEventMap['hubDisconnected']) {
      setHubStatus('disconnected');
      setHubBatteryLevel(undefined);
    }

    const service = isHubFlow ? hubSensorService : sensorService;

    // Add event listeners for emitter state changes
    service.on('batteryUpdate', handleBatteryUpdate);
    service.on('deviceConnected', handleDeviceConnected);
    service.on('deviceDisconnected', handleDeviceDisconnected);
    service.on('hubBatteryUpdate', handleHubBatteryUpdate);
    service.on('hubDisconnected', handleHubDisconnected);

    return () => {
      // Remove event listeners for emitter state changes
      service.off('batteryUpdate', handleBatteryUpdate);
      service.off('deviceConnected', handleDeviceConnected);
      service.off('deviceDisconnected', handleDeviceDisconnected);
      service.off('hubBatteryUpdate', handleHubBatteryUpdate);
      service.off('hubDisconnected', handleHubDisconnected);
    };
  }, [isHubFlow]);

  return (
    <SensorsContext.Provider
      value={{
        state,
        disconnectSensors,
        finishCalibration,
        hubBatteryLevel,
        hubStatus,
        markSensorFailed,
        markSensorWorking,
        reconfigureSensors,
        reset,
        resetCalibration,
        sensorConnected,
        sensorFailedCalibration,
        setAllConnected,
        setAllSynced,
        setBatteryLevel,
        setHubStatus,
        setSensorsInDatabase,
        setNetworkKey,
        setDeviceState,
        startCalibration,
        setRecording,
        setSessionStarted,
      }}>
      {children}
    </SensorsContext.Provider>
  );
};

export const useSensorsContext = (): SensorsContextType => {
  const context = useContext(SensorsContext);

  if (!context) {
    throw new Error('useSensorsContext must be used within an SensorsProvider');
  }

  return context;
};
